import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userService } from '../services/userService';
import { TeamMember, TeamMembersResponse } from '../types/api';

interface TeamState {
  members: TeamMember[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  searchText: string;
  updateUserLoading: boolean;
  updateUserError: string | null;
  updateUserSuccess: string | null;
  deleteUserLoading: boolean;
  deleteUserError: string | null;
  deleteUserSuccess: string | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalElements: number;
    isFirst: boolean;
    isLast: boolean;
  };
}

const initialState: TeamState = {
  members: [],
  loading: false,
  loadingMore: false,
  error: null,
  searchText: '',
  updateUserLoading: false,
  updateUserError: null,
  updateUserSuccess: null,
  deleteUserLoading: false,
  deleteUserError: null,
  deleteUserSuccess: null,
  pagination: {
    currentPage: 0,
    pageSize: 10,
    totalPages: 0,
    totalElements: 0,
    isFirst: true,
    isLast: true,
  },
};

// Async thunk for fetching team members
export const fetchTeamMembers = createAsyncThunk(
  'team/fetchTeamMembers',
  async (
    {
      searchText = '',
      tenantId,
    }: {
      pageNo?: number;
      pageSize?: number;
      searchText?: string;
      tenantId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await userService.getTeamMembers(searchText, tenantId);
      return response.data;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch team members';
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for loading more team members (infinite scroll)
export const loadMoreTeamMembers = createAsyncThunk(
  'team/loadMoreTeamMembers',
  async (
    {
      searchText = '',
      tenantId,
    }: {
      pageNo?: number;
      pageSize?: number;
      searchText?: string;
      tenantId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await userService.getTeamMembers(searchText, tenantId);
      return response.data;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to load more team members';
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for updating user profile
export const updateUserProfile = createAsyncThunk(
  'team/updateUserProfile',
  async (
    {
      userData,
      tenantId,
    }: {
      userData: {
        uuid: string;
        roleName: string;
        ehrUsername?: string;
      };
      tenantId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await userService.updateUserProfile(userData, tenantId);
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update user profile';
      return rejectWithValue(errorMessage);
    }
  },
);

// Async thunk for deleting user
export const deleteUser = createAsyncThunk(
  'team/deleteUser',
  async (
    {
      uuid,
      tenantId,
    }: {
      uuid: string;
      tenantId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await userService.deleteUser(uuid, tenantId);
      return { response, uuid };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete user';
      return rejectWithValue(errorMessage);
    }
  },
);

const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    clearTeamMembers: (state) => {
      state.members = [];
      state.error = null;
      state.searchText = '';
      state.pagination = initialState.pagination;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
    },
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
      state.pagination.currentPage = 0; // Reset to first page when searching
      state.members = []; // Clear existing members when searching
    },
    resetUpdateUserError: (state) => {
      state.updateUserError = null;
    },
    resetUpdateUserSuccess: (state) => {
      state.updateUserSuccess = null;
    },
    resetDeleteUserError: (state) => {
      state.deleteUserError = null;
    },
    resetDeleteUserSuccess: (state) => {
      state.deleteUserSuccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle initial fetch
      .addCase(fetchTeamMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchTeamMembers.fulfilled,
        (state, action: PayloadAction<TeamMembersResponse>) => {
          state.loading = false;
          state.members = action.payload.models;
          state.pagination = {
            currentPage: 0,
            pageSize: state.pagination.pageSize,
            totalPages: action.payload.totalPages,
            totalElements: action.payload.totalElements,
            isFirst: action.payload.isFirst,
            isLast: action.payload.isLast,
          };
        },
      )
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || 'Failed to fetch team members';
      })
      // Handle load more (infinite scroll)
      .addCase(loadMoreTeamMembers.pending, (state) => {
        state.loadingMore = true;
        state.error = null;
      })
      .addCase(
        loadMoreTeamMembers.fulfilled,
        (state, action: PayloadAction<TeamMembersResponse>) => {
          state.loadingMore = false;
          // Append new members to existing ones
          state.members = [...state.members, ...action.payload.models];
          state.pagination = {
            currentPage: state.pagination.currentPage + 1,
            pageSize: state.pagination.pageSize,
            totalPages: action.payload.totalPages,
            totalElements: action.payload.totalElements,
            isFirst: action.payload.isFirst,
            isLast: action.payload.isLast,
          };
        },
      )
      .addCase(loadMoreTeamMembers.rejected, (state, action) => {
        state.loadingMore = false;
        state.error =
          (action.payload as string) || 'Failed to load more team members';
      })
      // Handle update user profile
      .addCase(updateUserProfile.pending, (state) => {
        state.updateUserLoading = true;
        state.updateUserError = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.updateUserLoading = false;
        state.updateUserSuccess = action.payload.message;
        // Update the user in the members list if the update was successful
        if (action.payload.success) {
          const updatedUser = action.meta.arg.userData;
          const userIndex = state.members.findIndex(
            (member) => member.uuid === updatedUser.uuid,
          );
          if (userIndex !== -1) {
            state.members[userIndex] = {
              ...state.members[userIndex],
              role: updatedUser.roleName,
              ehrUsername:
                updatedUser.ehrUsername ?? state.members[userIndex].ehrUsername,
            };
          }
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.updateUserLoading = false;
        state.updateUserError =
          (action.payload as string) || 'Failed to update user profile';
      })
      // Handle delete user
      .addCase(deleteUser.pending, (state) => {
        state.deleteUserLoading = true;
        state.deleteUserError = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.deleteUserLoading = false;
        state.deleteUserSuccess = action.payload.response.message;
        // Remove the user from the members list if the delete was successful
        if (action.payload.response.success) {
          const deletedUuid = action.payload.uuid;
          state.members = state.members.filter(
            (member) => member.uuid !== deletedUuid,
          );
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deleteUserLoading = false;
        state.deleteUserError =
          (action.payload as string) || 'Failed to delete user';
      });
  },
});

export const {
  clearTeamMembers,
  setCurrentPage,
  setPageSize,
  setSearchText,
  resetUpdateUserError,
  resetUpdateUserSuccess,
  resetDeleteUserError,
  resetDeleteUserSuccess,
} = teamSlice.actions;
export default teamSlice.reducer;
